import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import { addDaysYmd, coerceAnalyticsWindow } from '@/lib/analyticsWindow';

function normalizeDateKey(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'yyyy-MM-dd');
}

function UsageActivityTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const raw = payload[0]?.value ?? row.minutes;
  const mins = Math.round(Number(raw ?? 0));
  const unit = mins === 1 ? 'minute' : 'minutes';
  let dateStr = '';
  if (row.key) {
    const day = parseISO(`${row.key}T12:00:00`);
    if (!Number.isNaN(day.getTime())) dateStr = format(day, 'MMM d, yyyy');
  }
  if (!dateStr) dateStr = row.date ? String(row.date) : '';

  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-md border-0"
      style={{
        backgroundColor: 'rgba(0, 194, 194, 0.95)',
        color: 'white',
      }}
    >
      <p className="font-semibold tabular-nums text-sm leading-tight">
        {mins} {unit}
      </p>
      {dateStr ? <p className="text-[11px] text-white/80 mt-1 tabular-nums">{dateStr}</p> : null}
    </div>
  );
}

export default function UsageChart({ sessions, timeRange, analyticsWindow, dailyData: dailyDataProp }) {
  const window = coerceAnalyticsWindow(analyticsWindow || timeRange);
  const [selectedSummary, setSelectedSummary] = useState(null);

  const dailyData = useMemo(() => {
    const emptySeries = [];
    if (window.mode === 'custom') {
      let cursor = window.from;
      while (cursor <= window.to) {
        const day = parseISO(`${cursor}T12:00:00`);
        emptySeries.push({
          key: cursor,
          date: Number.isNaN(day.getTime()) ? cursor : format(day, 'MMM d'),
          minutes: 0,
        });
        cursor = addDaysYmd(cursor, 1);
      }
    } else {
      const days = parseInt(window.days, 10) || 30;
      for (let i = days - 1; i >= 0; i--) {
        const date = subDays(new Date(), i);
        emptySeries.push({
          key: format(date, 'yyyy-MM-dd'),
          date: format(date, 'MMM d'),
          minutes: 0
        });
      }
    }

    // Prefer backend-provided daily aggregates when available.
    if (Array.isArray(dailyDataProp) && dailyDataProp.length > 0) {
      const minutesByDay = new Map();

      dailyDataProp.forEach((entry) => {
        const key = normalizeDateKey(
          entry.date || entry.day || entry.bucket || entry.session_date || entry.created_at
        );
        if (!key) return;
        const rawMinutes =
          entry.minutes ??
          entry.total_minutes ??
          entry.duration_minutes ??
          entry.value ??
          0;
        const minutes = Number(rawMinutes);
        minutesByDay.set(key, Number.isFinite(minutes) ? Math.round(minutes) : 0);
      });

      return emptySeries.map((item) => ({
        ...item,
        minutes: minutesByDay.get(item.key) ?? 0
      }));
    }

    const safeSessions = Array.isArray(sessions) ? sessions : [];
    return emptySeries.map((item) => {
      const totalMinutes = safeSessions
        .filter((s) => normalizeDateKey(s.session_start || s.started_at || s.created_at) === item.key)
        .reduce((sum, s) => sum + Number(s.duration_minutes || s.minutes || 0), 0);

      return {
        ...item,
        minutes: Math.round(totalMinutes)
      };
    });
  }, [window, dailyDataProp, sessions]);

  const handleBarClick = (entry, index) => {
    const payload = entry?.payload ?? dailyData[index];
    if (!payload?.key) return;
    const day = parseISO(`${payload.key}T12:00:00`);
    const label = Number.isNaN(day.getTime()) ? payload.date : format(day, 'MMM d, yyyy');
    const mins = Math.round(Number(payload.minutes ?? 0));
    const unit = mins === 1 ? 'minute' : 'minutes';
    setSelectedSummary(`${label} — ${mins} ${unit}`);
  };

  const hasAnyData = dailyData.some((item) => item.minutes > 0);

  if (!hasAnyData) {
    return (
      <div className="h-32 flex items-center justify-center text-xs text-gray-500 border border-dashed rounded-md">
        No daily activity data available yet
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dailyData}>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 10 }} width={30} />
            <Tooltip content={<UsageActivityTooltip />} cursor={{ fill: 'rgba(0, 194, 194, 0.12)' }} />
            <Bar
              dataKey="minutes"
              fill="#00C2C2"
              radius={[4, 4, 0, 0]}
              cursor="pointer"
              onClick={handleBarClick}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {selectedSummary ? (
        <p className="text-xs text-gray-600 mt-1 text-center tabular-nums" role="status">
          {selectedSummary}
        </p>
      ) : null}
    </div>
  );
}